import type { Side } from "@design-lab/shared/types";
import { track, trackRudderstack } from "./trackEvent";
import type { DesignLabUploadEventOptions } from "./upload.types";

export type { DesignLabUploadEventOptions } from "./upload.types";

export const trackDesignLabSessionStart = () => {
  const timestamp = Date.now();

  track("design_lab_session_started", {
    timestamp
  });
};

export const trackDesignUploadStarted = (
  placement: Side,
  source: "upload" | "design_library" | "canva" | "ai_generation" | "camera",
  options?: Partial<DesignLabUploadEventOptions>
) => {
  const basePayload = {
    placement,
    source,
    timestamp: Date.now(),
    ...(options?.uploadUuid && { upload_uuid: options.uploadUuid })
  };

  // Add file information if available
  const filePayload = options?.file
    ? {
        file_name: options.file.name,
        file_type: options.file.type,
        file_size: options.file.size
      }
    : {
        // For design library selections, file_type may be provided directly
        ...(options?.fileType && { file_type: options.fileType })
      };

  // Add business context if available
  const businessPayload = {
    ...(options?.catalogNumber && { catalog_number: options.catalogNumber }),
    ...(options?.businessLine && { business_line: options.businessLine }),
    ...(options?.orderType && { order_type: options.orderType }),
    ...(options?.page && { page: options.page }),
    ...(options?.pdpType && { pdp_type: options.pdpType }),
    ...(options?.projectId !== undefined && { project_id: options.projectId }),
    ...(options?.multiArtwork !== undefined && { multi_artwork: options.multiArtwork }),
    ...(options?.artworksCount !== undefined && { artworks_count: options.artworksCount }),
    ...(options?.dpi && { dpi: options.dpi }),
    ...(options?.preCutTransfers !== undefined && { pre_cut_transfers: options.preCutTransfers })
  };

  track("design_lab_upload_started", {
    ...basePayload,
    ...filePayload,
    ...businessPayload
  });

  trackRudderstack(
    "Pilot: Image Upload Started",
    {
      ...basePayload,
      ...filePayload,
      ...businessPayload
    },
    true
  );
};

// MN-6259: the failure event now flows to Klaviyo, which interpolates event
// properties into outbound recovery emails. file_name (user-chosen) and
// error_message (server/exception-derived) are untrusted, so strip Liquid/HTML
// template metacharacters and cap length before forwarding — neutralizes the
// injection vector and bounds any accidental internal-string leak. The bounded
// `type` enum remains what Klaviyo flows should actually key on.
const KLAVIYO_FIELD_MAX_LENGTH = 200;
const sanitizeForKlaviyo = (value: string): string =>
  value.replace(/[<>{}%]/g, "").slice(0, KLAVIYO_FIELD_MAX_LENGTH);

export const trackDesignUploadFailed = (
  options: {
    type: "validation_error" | "unhandled_error" | "timeout_error";
    errorMessage: string;
  },
  context?: Partial<DesignLabUploadEventOptions>
) => {
  const basePayload = {
    type: options.type,
    error_message: sanitizeForKlaviyo(options.errorMessage),
    timestamp: Date.now(),
    ...(context?.uploadUuid && { upload_uuid: context.uploadUuid })
  };

  // Carry the same business/file context the started/completed events emit, so the
  // Klaviyo upload-issue recovery flow can segment by business_line (e.g. DTF only)
  // and by what the user was doing (placement/source) when the upload failed. A
  // failure event with no context reaches Klaviyo but can never be targeted.
  const filePayload = context?.file
    ? {
        file_name: sanitizeForKlaviyo(context.file.name),
        file_type: context.file.type,
        file_size: context.file.size
      }
    : {
        ...(context?.fileType && { file_type: context.fileType })
      };

  const businessPayload = {
    ...(context?.placement && { placement: context.placement }),
    ...(context?.source && { source: context.source }),
    ...(context?.catalogNumber && { catalog_number: context.catalogNumber }),
    ...(context?.businessLine && { business_line: context.businessLine }),
    ...(context?.orderType && { order_type: context.orderType }),
    ...(context?.page && { page: context.page }),
    ...(context?.pdpType && { pdp_type: context.pdpType }),
    ...(context?.dpi && { dpi: context.dpi })
  };

  const payload = { ...basePayload, ...filePayload, ...businessPayload };

  // This runs in useArtworkImage's upload catch block, before the state reset and
  // the rethrow of the original error. Keep each sink strictly best-effort and
  // independent: a throw from either SDK would otherwise mask the real upload error
  // and skip the UI reset (stuck in-progress), and a Mixpanel failure must not
  // suppress the Rudderstack mirror — that mirror is what drives the Klaviyo
  // upload-recovery flow this event was added for.
  try {
    track("design_lab_upload_failed", payload);
  } catch {
    // swallow — analytics must never affect upload error handling
  }

  // Mirror to Rudderstack so the failure reaches Klaviyo — its sibling
  // started/completed events already dual-dispatch (see trackDesignUploadStarted
  // / trackDesignUploadCompleted).
  try {
    trackRudderstack("Pilot: Image Upload Failed", payload, true);
  } catch {
    // swallow — analytics must never affect upload error handling
  }
};

/* eslint-disable max-params */
export const trackDesignUploadCompleted = (
  placement: Side,
  artworkId: string,
  source: "upload" | "design_library" | "canva" | "ai_generation" | "camera",
  options?: Partial<DesignLabUploadEventOptions>
) => {
  const basePayload = {
    placement,
    artwork_id: artworkId,
    source,
    timestamp: Date.now(),
    ...(options?.uploadUuid && { upload_uuid: options.uploadUuid })
  };

  // Add file information if available
  const filePayload = options?.file
    ? {
        file_name: options.file.name,
        file_type: options.file.type,
        file_size: options.file.size
      }
    : {};

  // Add business context if available
  const businessPayload = {
    ...(options?.catalogNumber && { catalog_number: options.catalogNumber }),
    ...(options?.businessLine && { business_line: options.businessLine }),
    ...(options?.orderType && { order_type: options.orderType }),
    ...(options?.page && { page: options.page }),
    ...(options?.pdpType && { pdp_type: options.pdpType }),
    ...(options?.projectId !== undefined && { project_id: options.projectId }),
    ...(options?.multiArtwork !== undefined && { multi_artwork: options.multiArtwork }),
    ...(options?.artworksCount !== undefined && { artworks_count: options.artworksCount }),
    ...(options?.dpi && { dpi: options.dpi }),
    ...(options?.preCutTransfers !== undefined && { pre_cut_transfers: options.preCutTransfers })
  };

  // Add performance metrics if available
  const performancePayload = options?.uploadStartTime
    ? {
        upload_speed_megabits_per_second: calculateUploadSpeed(
          options.file,
          options.uploadStartTime
        )
      }
    : {};

  track("design_lab_upload_completed", {
    ...basePayload,
    ...filePayload,
    ...businessPayload,
    ...performancePayload
  });

  trackRudderstack(
    "Pilot: Image Upload Completed",
    {
      ...basePayload,
      ...filePayload,
      ...businessPayload,
      ...performancePayload
    },
    true
  );
};

// Helper function to calculate upload speed (similar to submit-dtf-events.js)
const calculateUploadSpeed = (file: File | undefined, startTime: number): number | undefined => {
  if (!file || !startTime) return undefined;

  const duration = performance.now() - startTime;
  if (duration <= 0) return undefined;

  // Convert bytes to megabits and calculate speed
  const sizeInMegabits = (file.size * 8) / (1024 * 1024);
  const speedInMegabitsPerSecond = sizeInMegabits / (duration / 1000);

  return Math.round(speedInMegabitsPerSecond * 100) / 100; // Round to 2 decimal places
};
